/** Шторка снизу и диалог.
 *
 * Шторка: ручка сверху, баланс в шапке, действия — в нижней половине,
 * под большим пальцем. Затемнение фона 40%, чтобы доска оставалась видна:
 * игрок должен помнить позицию, ради которой открыл шторку.
 *
 * Диалог отличается только тем, что не приклеен к низу и не имеет ручки:
 * его закрывают кнопкой, а не свайпом, потому что выбор в нём обязателен.
 */

import type { ReactNode } from 'react'
import { useEffect } from 'react'

interface OverlayProps {
  children: ReactNode
  /** Закрытие по фону. У обязательного выбора его нет. */
  onDismiss?: () => void
  variant?: 'sheet' | 'dialog'
}

function Overlay({ children, onDismiss, variant = 'sheet' }: OverlayProps) {
  // Пока открыта шторка, фон прокручиваться не должен: иначе экран уезжает
  // под пальцем, который тянется к кнопке внизу.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div
      className={`overlay ${variant === 'dialog' ? 'overlay--dialog' : ''}`.trim()}
      onClick={onDismiss}
      role="presentation"
    >
      <div onClick={(event) => event.stopPropagation()} role="presentation">
        {children}
      </div>
    </div>
  )
}

export function Sheet({
  title,
  right,
  children,
  onDismiss,
  variant,
}: {
  title?: string
  right?: ReactNode
  children: ReactNode
  onDismiss?: () => void
  variant?: 'result'
}) {
  return (
    <Overlay onDismiss={onDismiss}>
      <div className={`sheet ${variant === 'result' ? 'sheet--result' : ''}`.trim()}>
        {variant !== 'result' && <div className="sheet__handle" />}
        {title && (
          <div className="sheet__head">
            <span className="sheet__title">{title}</span>
            {right}
          </div>
        )}
        {children}
      </div>
    </Overlay>
  )
}

export function Dialog({
  title,
  children,
  onDismiss,
}: {
  title: string
  children: ReactNode
  onDismiss?: () => void
}) {
  return (
    <Overlay onDismiss={onDismiss} variant="dialog">
      <div className="dialog">
        <span className="dialog__title">{title}</span>
        {children}
      </div>
    </Overlay>
  )
}
